import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  RefreshCw,
  ChevronLeft,
  Plus,
  Trash2,
  Edit,
  X,
  FileText,
  ListChecks,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Loader2,
  Sparkles
} from 'lucide-react';
import http from '../api/http';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';

/* ------------------------------------------------------------------ */
/*  Styles partagés                                                    */
/* ------------------------------------------------------------------ */

const inputClass =
  'w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-xl bg-white placeholder-gray-400 ' +
  'focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all';

const labelClass = 'mb-1.5 block text-sm font-medium text-gray-700';

const primaryBtn =
  'inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium bg-blue-600 text-white ' +
  'rounded-xl shadow-sm hover:bg-blue-700 active:scale-[0.98] transition-all ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:ring-offset-2 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100';

const purpleBtn =
  'inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-medium bg-purple-600 text-white ' +
  'rounded-xl shadow-sm hover:bg-purple-700 active:scale-[0.98] transition-all ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500/40 focus-visible:ring-offset-2 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100';

const secondaryBtn =
  'inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium bg-white text-gray-700 ' +
  'border border-gray-200 rounded-xl hover:bg-gray-50 active:scale-[0.98] transition-all ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/30';

const keyframes = `
@keyframes cp-modal-in { from { opacity: 0; transform: translateY(8px) scale(.98); } to { opacity: 1; transform: none; } }
@keyframes cp-fade-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes cp-toast-in { from { opacity: 0; transform: translateX(16px); } to { opacity: 1; transform: none; } }
.cp-modal-in { animation: cp-modal-in .18s ease-out; }
.cp-fade-in { animation: cp-fade-in .15s ease-out; }
.cp-toast-in { animation: cp-toast-in .2s ease-out; }
@media (prefers-reduced-motion: reduce) {
  .cp-modal-in, .cp-fade-in, .cp-toast-in { animation: none; }
}
`;

const COMPARATEURS = [
  { value: '==', label: 'Égal à', symbol: '=' },
  { value: '<', label: 'Inférieur à', symbol: '<' },
  { value: '<=', label: 'Inférieur ou égal à', symbol: '≤' },
  { value: '>', label: 'Supérieur à', symbol: '>' },
  { value: '>=', label: 'Supérieur ou égal à', symbol: '≥' }
];

const OPERATEURS = { '||': 'OU', '&&': 'ET' };

/* ------------------------------------------------------------------ */
/*  Petits composants                                                  */
/* ------------------------------------------------------------------ */

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-4 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
      <Icon className="h-5 w-5" />
    </div>
    <div className="min-w-0">
      <div className="text-2xl font-semibold leading-tight text-gray-900">{value}</div>
      <div className="truncate text-sm text-gray-500">{label}</div>
    </div>
  </div>
);

const SkeletonCard = () => (
  <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
    <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
      <div className="h-5 w-40 animate-pulse rounded bg-gray-100" />
      <div className="h-9 w-40 animate-pulse rounded-xl bg-gray-100" />
    </div>
    <div className="space-y-4 px-6 py-5">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-4 animate-pulse rounded bg-gray-100" style={{ width: `${90 - i * 15}%` }} />
      ))}
    </div>
  </div>
);

const Modal = ({ title, subtitle, onClose, maxWidth = 'max-w-md', accent = 'blue', children }) => {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const head =
    accent === 'purple'
      ? 'border-purple-100 from-purple-50'
      : 'border-blue-100 from-blue-50';
  const titleColor = accent === 'purple' ? 'text-purple-900' : 'text-blue-900';
  const frame = accent === 'purple' ? 'border-purple-100' : 'border-blue-100';

  return (
    <div
      className="cp-fade-in fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        className={`cp-modal-in flex max-h-[92vh] w-full ${maxWidth} flex-col overflow-hidden rounded-2xl border ${frame} bg-white shadow-2xl`}
      >
        <div className={`flex shrink-0 items-start justify-between gap-4 border-b bg-gradient-to-r to-white px-6 py-4 ${head}`}>
          <div>
            <h3 className={`text-lg font-bold ${titleColor}`}>{title}</h3>
            {subtitle && <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-white hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

const ConditionsPaie = () => {
  const { typeId } = useParams();
  const navigate = useNavigate();
  const { filialeId, isConsolidated } = useFiliale();

  const [typePersonnel, setTypePersonnel] = useState(null);
  const [typesContrat, setTypesContrat] = useState([]);
  const [indicateurs, setIndicateurs] = useState([]);
  const [conditions, setConditions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedContrat, setSelectedContrat] = useState(null);
  const [editingCondition, setEditingCondition] = useState(null);
  const [formData, setFormData] = useState({
    Libelle_condition: '',
    valeur_vrai: 0,
    valeur_fausse: 0
  });
  const [detailConditions, setDetailConditions] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState(null);

  /* ---------- Notifications (remplace alert) ---------- */
  const notify = useCallback((type, message) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, type, message }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3800);
  }, []);

  useEffect(() => {
    loadData();
  }, [typeId, filialeId]);

  /* ---------- Chargement ---------- */
  const loadData = async () => {
    try {
      setLoading(true);

      const [typesRes, contratsRes, indicateursRes] = await Promise.all([
        http.get('/api/types-personnel').catch(() => ({ data: [] })),
        http.get('/api/types-contrat').catch(() => ({ data: [] })),
        http.get(`/api/indicateur-association/${typeId}`).catch(() => ({ data: [] }))
      ]);

      const allTypes = Array.isArray(typesRes.data) ? typesRes.data : [];
      const type = allTypes.find((t) => t.idtype_personnel === parseInt(typeId));
      setTypePersonnel(type);

      const allContrats = Array.isArray(contratsRes.data) ? contratsRes.data : [];
      setTypesContrat(allContrats);

      const allIndicateurs = Array.isArray(indicateursRes.data) ? indicateursRes.data : [];
      setIndicateurs(allIndicateurs);

      // Récupérer les conditions pour chaque contrat
      const conditionsData = {};
      for (const contrat of allContrats) {
        const condRes = await http
          .get(`/api/conditions-paie/${typeId}/${contrat.idtype_contrat}`)
          .catch(() => ({ data: [] }));
        conditionsData[contrat.idtype_contrat] = Array.isArray(condRes.data) ? condRes.data : [];
      }
      setConditions(conditionsData);
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  };

  /* ---------- Actions ---------- */
  const handleAddCondition = (contrat) => {
    setSelectedContrat(contrat);
    setEditingCondition(null);
    setFormData({
      Libelle_condition: '',
      valeur_vrai: 0,
      valeur_fausse: 0
    });
    setDetailConditions([]);
    setShowModal(true);
  };

  const handleEditCondition = async (condition) => {
    try {
      // Récupérer les détails de la condition
      const detailsRes = await http.get(`/api/detail-conditions/${condition.idcondition}`);
      const details = Array.isArray(detailsRes.data) ? detailsRes.data : [];

      setSelectedContrat(typesContrat.find((c) => c.idtype_contrat === condition.idtype_contrat));
      setEditingCondition(condition);
      setFormData({
        Libelle_condition: condition.Libelle_condition,
        valeur_vrai: condition.valeur_vrai,
        valeur_fausse: condition.valeur_fausse
      });
      setDetailConditions(
        details.map((d) => ({
          iddetail_condition: d.iddetail_condition,
          champ1: d.champ1,
          comparateur: d.comparateur,
          champ2: d.champ2,
          operateur_logique: d.operateur_logique
        }))
      );
      setShowModal(true);
    } catch (error) {
      console.error('Erreur chargement détails:', error);
      notify('error', 'Erreur lors du chargement des détails');
    }
  };

  const handleDeleteCondition = (idcondition) => {
    setConfirmState({
      title: 'Supprimer cette condition ?',
      message: 'La condition et tous ses détails seront supprimés. Cette action est irréversible.',
      onConfirm: async () => {
        try {
          await http.delete(`/api/conditions-paie/${idcondition}`);
          notify('success', 'Condition supprimée avec succès');
          loadData();
        } catch (error) {
          console.error('Erreur suppression:', error);
          notify('error', 'Erreur lors de la suppression');
        }
      }
    });
  };

  const handleAddDetail = () => {
    setDetailConditions([
      ...detailConditions,
      {
        champ1: '',
        comparateur: '==',
        champ2: '',
        operateur_logique: ''
      }
    ]);
  };

  const handleDetailChange = (index, field, value) => {
    setDetailConditions((prev) => prev.map((d, i) => (i === index ? { ...d, [field]: value } : d)));
  };

  const handleRemoveDetail = (index) => {
    setDetailConditions(detailConditions.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (saving) return;
    try {
      setSaving(true);
      let idcondition;

      if (editingCondition) {
        // Mise à jour de la condition principale
        await http.put(`/api/conditions-paie/${editingCondition.idcondition}`, {
          Libelle_condition: formData.Libelle_condition,
          valeur_vrai: parseInt(formData.valeur_vrai),
          valeur_fausse: parseInt(formData.valeur_fausse)
        });
        idcondition = editingCondition.idcondition;

        // Récupérer les détails existants
        const detailsRes = await http.get(`/api/detail-conditions/${idcondition}`);
        const existingDetails = Array.isArray(detailsRes.data) ? detailsRes.data : [];

        // Supprimer uniquement les détails qui ne sont plus dans la liste
        for (const existing of existingDetails) {
          const stillExists = detailConditions.some((d) => d.iddetail_condition === existing.iddetail_condition);
          if (!stillExists) {
            await http.delete(`/api/detail-conditions/${existing.iddetail_condition}`);
          }
        }

        // Créer ou mettre à jour les détails
        for (const detail of detailConditions) {
          if (detail.iddetail_condition) {
            // Mise à jour d'un détail existant
            await http.put(`/api/detail-conditions/${detail.iddetail_condition}`, {
              champ1: detail.champ1,
              comparateur: detail.comparateur,
              champ2: detail.champ2,
              operateur_logique: detail.operateur_logique
            });
          } else {
            // Création d'un nouveau détail
            await http.post('/api/detail-conditions', {
              idcondition: idcondition,
              champ1: detail.champ1,
              comparateur: detail.comparateur,
              champ2: detail.champ2,
              operateur_logique: detail.operateur_logique,
              filiale_id: filialeId || 1
            });
          }
        }
      } else {
        // Création de la condition principale
        const conditionRes = await http.post('/api/conditions-paie', {
          idtype_personnel: parseInt(typeId),
          idtype_contrat: selectedContrat.idtype_contrat,
          Libelle_condition: formData.Libelle_condition,
          valeur_vrai: parseInt(formData.valeur_vrai),
          valeur_fausse: parseInt(formData.valeur_fausse),
          filiale_id: filialeId || 1
        });
        idcondition = conditionRes.data.idcondition;

        // Créer les détails de condition
        for (const detail of detailConditions) {
          await http.post('/api/detail-conditions', {
            idcondition: idcondition,
            champ1: detail.champ1,
            comparateur: detail.comparateur,
            champ2: detail.champ2,
            operateur_logique: detail.operateur_logique,
            filiale_id: filialeId || 1
          });
        }
      }

      setShowModal(false);
      notify('success', editingCondition ? 'Condition modifiée avec succès' : 'Condition créée avec succès');
      loadData();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      notify('error', 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Aperçu lisible de la condition ---------- */
  const indicateurLabel = (id) => {
    const found = indicateurs.find((i) => String(i.idindicateur) === String(id));
    return found ? found.Libelle_indicateur : null;
  };

  const previewParts = detailConditions
    .map((d, i) => ({ d, i }))
    .filter(({ d }) => d.champ1)
    .map(({ d, i }) => {
      const cmp = COMPARATEURS.find((c) => c.value === d.comparateur);
      return {
        key: i,
        text: `${indicateurLabel(d.champ1) || 'Indicateur'} ${cmp ? cmp.symbol : d.comparateur} ${d.champ2 || '…'}`,
        op: i < detailConditions.length - 1 ? OPERATEURS[d.operateur_logique] : null
      };
    });

  /* ---------- Stats ---------- */
  const totalConditions = Object.values(conditions).reduce((sum, list) => sum + (list?.length || 0), 0);

  /* ---------- Rendu ---------- */
  return (
    <div className="min-h-screen bg-gray-50">
      <style>{keyframes}</style>

      <BlueHeader
        title={`Conditions de Paie : ${typePersonnel?.libelle || 'Type Personnel'}`}
        subtitle="Définir les conditions par type de contrat"
        actions={[
          {
            label: 'Retour',
            icon: ChevronLeft,
            onClick: () => navigate(`/types-personnel/${typePersonnel?.idactivite || 1}`),
            primary: false
          },
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: loadData,
            primary: false
          }
        ]}
      />

      <div className="space-y-6 px-4 py-6 sm:px-6 sm:py-8">
        {/* Statistiques */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard icon={FileText} label="Types de contrat" value={loading ? '–' : typesContrat.length} />
          <StatCard icon={ListChecks} label="Conditions définies" value={loading ? '–' : totalConditions} />
          <StatCard icon={Sparkles} label="Indicateurs disponibles" value={loading ? '–' : indicateurs.length} />
        </div>

        {/* Un bloc par type de contrat */}
        {loading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : typesContrat.length === 0 ? (
          <div className="rounded-2xl border border-gray-100 bg-white px-6 py-14 text-center shadow-sm">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-500">
              <FileText className="h-7 w-7" />
            </div>
            <h4 className="text-base font-semibold text-gray-800">Aucun type de contrat</h4>
            <p className="mx-auto mt-1 max-w-sm text-sm text-gray-500">
              Créez d'abord un type de contrat pour pouvoir définir des conditions de paie.
            </p>
          </div>
        ) : (
          typesContrat.map((contrat) => {
            const list = conditions[contrat.idtype_contrat] || [];
            return (
              <section
                key={contrat.idtype_contrat}
                className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm"
              >
                <div className="flex flex-col gap-3 border-b border-gray-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                      <FileText className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">{contrat.nom}</h3>
                      <p className="text-xs text-gray-500">
                        {list.length === 0
                          ? 'Aucune condition'
                          : `${list.length} condition${list.length > 1 ? 's' : ''}`}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => handleAddCondition(contrat)} className={primaryBtn}>
                    <Plus className="h-4 w-4" />
                    Nouvelle condition
                  </button>
                </div>

                {list.length === 0 ? (
                  <div className="flex flex-col items-center px-6 py-12 text-center">
                    <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-500">
                      <ListChecks className="h-6 w-6" />
                    </div>
                    <p className="text-sm font-medium text-gray-700">
                      Aucune condition définie pour ce type de contrat
                    </p>
                    <p className="mt-1 max-w-sm text-sm text-gray-500">
                      Une condition applique une valeur à la paie selon le résultat d'un ou plusieurs indicateurs.
                    </p>
                    <button onClick={() => handleAddCondition(contrat)} className={`${secondaryBtn} mt-4`}>
                      <Plus className="h-4 w-4" />
                      Ajouter la première condition
                    </button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50/80 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                          <th className="px-6 py-3">#</th>
                          <th className="px-6 py-3">Condition</th>
                          <th className="px-6 py-3">Valeur vrai</th>
                          <th className="px-6 py-3">Valeur fausse</th>
                          <th className="px-6 py-3 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {list.map((cond, index) => (
                          <tr key={cond.idcondition} className="transition-colors hover:bg-blue-50/40">
                            <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-400">{index + 1}</td>
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">{cond.Libelle_condition}</td>
                            <td className="whitespace-nowrap px-6 py-4">
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-800">
                                <CheckCircle2 className="h-3 w-3" />
                                {cond.valeur_vrai}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-6 py-4">
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                                <X className="h-3 w-3" />
                                {cond.valeur_fausse}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-6 py-4">
                              <div className="flex justify-center gap-1">
                                <button
                                  onClick={() => handleEditCondition(cond)}
                                  className="rounded-lg p-2 text-blue-600 transition-colors hover:bg-blue-100"
                                  title="Modifier"
                                  aria-label="Modifier"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteCondition(cond.idcondition)}
                                  className="rounded-lg p-2 text-red-500 transition-colors hover:bg-red-50"
                                  title="Supprimer"
                                  aria-label="Supprimer"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            );
          })
        )}
      </div>

      {/* Modal création / modification condition */}
      {showModal && (
        <Modal
          accent="purple"
          maxWidth="max-w-4xl"
          title={`${selectedContrat?.nom || ''} : ${editingCondition ? 'Modifier la condition' : 'Nouvelle condition'}`}
          subtitle="Définissez quand la condition est vraie, puis la valeur appliquée dans chaque cas."
          onClose={() => setShowModal(false)}
        >
          <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
            {/* Informations principales */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <label className={labelClass}>Titre de la condition</label>
                <input
                  type="text"
                  autoFocus
                  value={formData.Libelle_condition}
                  onChange={(e) => setFormData({ ...formData, Libelle_condition: e.target.value })}
                  className={inputClass}
                  placeholder="Ex: Prime d'assiduité"
                  required
                />
              </div>
              <div>
                <label className={labelClass}>
                  <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-green-500" />
                  Valeur si vrai
                </label>
                <input
                  type="number"
                  value={formData.valeur_vrai}
                  onChange={(e) => setFormData({ ...formData, valeur_vrai: parseInt(e.target.value) || 0 })}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>
                  <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-gray-400" />
                  Valeur si faux
                </label>
                <input
                  type="number"
                  value={formData.valeur_fausse}
                  onChange={(e) => setFormData({ ...formData, valeur_fausse: parseInt(e.target.value) || 0 })}
                  className={inputClass}
                />
              </div>
            </div>

            {/* Détails */}
            <div className="border-t border-gray-100 pt-5">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <h4 className="font-semibold text-gray-800">Détails de la condition</h4>
                  <p className="text-xs text-gray-500">Chaque ligne compare un indicateur à une valeur.</p>
                </div>
                <button
                  type="button"
                  onClick={handleAddDetail}
                  className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-3.5 py-2 text-sm font-medium text-white shadow-sm transition-all hover:bg-green-700 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500/40"
                >
                  <Plus className="h-4 w-4" />
                  Ajouter détail
                </button>
              </div>

              {detailConditions.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/60 px-4 py-8 text-center text-sm text-gray-500">
                  Aucun détail ajouté. Cliquez sur « Ajouter détail » pour définir la première règle.
                </div>
              ) : (
                <div className="space-y-3">
                  {detailConditions.map((detail, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-1 gap-3 rounded-xl border border-gray-200 bg-gray-50/50 p-3 sm:grid-cols-12 sm:items-end"
                    >
                      <div className="sm:col-span-4">
                        <label className="mb-1 block text-xs font-medium text-gray-500">Indicateur</label>
                        <select
                          value={detail.champ1}
                          onChange={(e) => handleDetailChange(index, 'champ1', e.target.value)}
                          className={inputClass}
                        >
                          <option value="">Sélectionner</option>
                          {indicateurs.map((ind) => (
                            <option key={ind.idindicateur} value={ind.idindicateur}>
                              {ind.Libelle_indicateur}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="sm:col-span-3">
                        <label className="mb-1 block text-xs font-medium text-gray-500">Comparateur</label>
                        <select
                          value={detail.comparateur}
                          onChange={(e) => handleDetailChange(index, 'comparateur', e.target.value)}
                          className={inputClass}
                        >
                          {COMPARATEURS.map((c) => (
                            <option key={c.value} value={c.value}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="sm:col-span-2">
                        <label className="mb-1 block text-xs font-medium text-gray-500">Valeur</label>
                        <input
                          type="text"
                          value={detail.champ2}
                          onChange={(e) => handleDetailChange(index, 'champ2', e.target.value)}
                          className={inputClass}
                          placeholder="Valeur"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="mb-1 block text-xs font-medium text-gray-500">Puis</label>
                        <select
                          value={detail.operateur_logique}
                          onChange={(e) => handleDetailChange(index, 'operateur_logique', e.target.value)}
                          className={inputClass}
                        >
                          <option value="">Aucun</option>
                          <option value="||">OU</option>
                          <option value="&&">ET</option>
                        </select>
                      </div>
                      <div className="flex justify-end sm:col-span-1">
                        <button
                          type="button"
                          onClick={() => handleRemoveDetail(index)}
                          className="rounded-lg p-2.5 text-red-500 transition-colors hover:bg-red-50"
                          title="Retirer ce détail"
                          aria-label="Retirer ce détail"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Aperçu en langage clair */}
              {previewParts.length > 0 && (
                <div className="mt-4 rounded-xl border border-purple-100 bg-purple-50/60 px-4 py-3 text-sm text-purple-900">
                  <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-purple-700">
                    <Sparkles className="h-3.5 w-3.5" />
                    Aperçu de la règle
                  </div>
                  <p className="leading-relaxed">
                    <span className="font-semibold">Si </span>
                    {previewParts.map((p) => (
                      <span key={p.key}>
                        <span className="rounded bg-white px-1.5 py-0.5 font-medium shadow-sm">{p.text}</span>
                        {p.op && <span className="mx-1.5 font-bold text-purple-600">{p.op}</span>}
                      </span>
                    ))}
                    <span className="font-semibold">, alors </span>
                    <span className="rounded bg-green-100 px-1.5 py-0.5 font-medium text-green-800">
                      {formData.valeur_vrai}
                    </span>
                    <span className="font-semibold"> sinon </span>
                    <span className="rounded bg-gray-200 px-1.5 py-0.5 font-medium text-gray-700">
                      {formData.valeur_fausse}
                    </span>
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="flex shrink-0 justify-end gap-3 border-t border-purple-100 bg-gray-50/60 px-6 py-4">
            <button type="button" onClick={() => setShowModal(false)} className={secondaryBtn}>
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !formData.Libelle_condition.trim()}
              className={purpleBtn}
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving ? 'Enregistrement…' : 'Valider'}
            </button>
          </div>
        </Modal>
      )}

      {/* Confirmation de suppression (remplace window.confirm) */}
      {confirmState && (
        <Modal title={confirmState.title} onClose={() => setConfirmState(null)}>
          <div className="flex items-start gap-3 px-6 py-5">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600">
              <AlertTriangle className="h-5 w-5" />
            </span>
            <p className="pt-1 text-sm text-gray-600">{confirmState.message}</p>
          </div>
          <div className="flex justify-end gap-3 border-t border-blue-100 bg-gray-50/60 px-6 py-4">
            <button type="button" onClick={() => setConfirmState(null)} className={secondaryBtn}>
              Annuler
            </button>
            <button
              type="button"
              onClick={() => {
                const { onConfirm } = confirmState;
                setConfirmState(null);
                onConfirm();
              }}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-red-700 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40"
            >
              <Trash2 className="h-4 w-4" />
              Supprimer
            </button>
          </div>
        </Modal>
      )}

      {/* Notifications */}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-2"
        aria-live="polite"
      >
        {toasts.map((t) => {
          const styles = {
            success: { box: 'border-green-200', icon: CheckCircle2, color: 'text-green-600' },
            error: { box: 'border-red-200', icon: AlertCircle, color: 'text-red-600' },
            warning: { box: 'border-amber-200', icon: AlertTriangle, color: 'text-amber-600' }
          }[t.type];
          const Icon = styles.icon;
          return (
            <div
              key={t.id}
              className={`cp-toast-in pointer-events-auto flex items-start gap-3 rounded-xl border bg-white px-4 py-3 shadow-lg ${styles.box}`}
            >
              <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${styles.color}`} />
              <p className="flex-1 text-sm text-gray-800">{t.message}</p>
              <button
                onClick={() => setToasts((all) => all.filter((x) => x.id !== t.id))}
                aria-label="Fermer la notification"
                className="text-gray-400 hover:text-gray-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ConditionsPaie;